/**
 * Transforms order J-codes into the Auth Letter Success modal's display
 * `metaData`, grouping codes by authorization number into cards.
 * @module buildAuthLetterSuccessMetaData
 */
import {
  AuthLetterSuccessModalMetaData,
  AuthorizationCardInfoProps,
  AuthorizationDetail,
  ServiceCardInfoCardProps,
} from "../../../../data-model/authLetterSuccessModel";
import { JCodeDetails } from "../../../../data-model/medicalPaOrdersModel";
import { formatDateToUTCWithFormat } from "../../../../utils/formatDate";

/**
 * Maps a single J-code entry to a service-code info card (code only).
 *
 * @param jCode - J-code auth detail entry to convert.
 * @returns Service-code card props for the success modal.
 */
const toServiceCardInfoCard = (
  jCode: JCodeDetails,
): ServiceCardInfoCardProps => ({
  code: jCode.j_code ?? "",
});

/**
 * Builds the label/value detail rows for one authorization group.
 *
 * @param jCodes - J-codes belonging to a single authorization.
 * @param payerName - Primary payer name shown on the card.
 * @param patientMrn - Patient MRN shown on the card.
 * @returns Ordered label/value pairs for the details grid.
 */
const buildAuthorizationDetails = (
  jCodes: JCodeDetails[],
  payerName: string,
  patientMrn: string,
): AuthorizationDetail[] => {
  const first = jCodes[0];
  const startDate = first?.start_date ?? "--";
  const expirationDate = first?.end_date ?? "--";
  return [
    { label: "Authorization Number", value: first?.auth_number ?? "--" },
    { label: "Payer Name", value: payerName || "--" },
    { label: "Patient MRN", value: patientMrn || "--" },
    { label: "Date Range", value: `${startDate} - ${expirationDate}` },
  ];
};

/**
 * Transforms J-codes into the success modal's `metaData`, grouping codes
 * that share an authorization number into a single authorization card.
 *
 * Mirrors the auth-letter upload modal's grouping so the read-only success
 * view reflects the same authorization structure that was written back.
 *
 * @param jCodes - J-code auth detail entries for the order.
 * @param payerName - Primary payer name to display on every card.
 * @param patientMrn - Patient MRN to display on every card.
 * @param candidRpaSuccessTimestamp - Timestamp of the successful Candid writeback.
 * @returns Metadata payload ready to dispatch to the success modal.
 */
export const buildAuthLetterSuccessMetaData = (
  jCodes: JCodeDetails[],
  payerName: string,
  patientMrn: string,
  candidRpaSuccessTimestamp: string,
): AuthLetterSuccessModalMetaData => {
  const grouped = new Map<string, JCodeDetails[]>();
  for (const jCode of jCodes) {
    const key = jCode.auth_number ?? "";
    const existing = grouped.get(key);
    if (existing) {
      existing.push(jCode);
    } else {
      grouped.set(key, [jCode]);
    }
  }

  const authorizations: AuthorizationCardInfoProps[] = Array.from(
    grouped.entries(),
  ).map(([, groupJCodes], index) => ({
    header: {
      title: `Authorization #${index + 1}`,
      status: "Success",
      date: formatDateToUTCWithFormat(
        candidRpaSuccessTimestamp,
        "MM/DD/YYYY HH:mm:ss",
      ),
      textColor: "#005D49",
      bgColor: "#E6F3F0",
    },
    details: buildAuthorizationDetails(groupJCodes, payerName, patientMrn),
    serviceCodes: groupJCodes.map(toServiceCardInfoCard),
  }));

  return { authorizations };
};
